import React from 'react'
import { Button, Input, Label, Separator } from '@/shared/ui'
import type { OpportunityLimits, OpportunityCycle, TimeframeKey, Plan } from '../types'
import { getLimitsFromPlan, validateAllocations } from '../services/opportunityLimits'
import { updatePlanMeta } from '../services/planAdminService'

type Props = {
  plan?: Plan | null
  onChanged?: () => void
}

const TF_LABEL: Record<TimeframeKey, string> = {
  '5': '5m',
  '15': '15m',
  '30': '30m',
  '60': '1h (Scalp)',
  '240': '4h (Day)',
  '720': '12h (Day+)',
  'D': '1D (Swing)',
  'W': '1W (Swing+)',
  'M': '1M (Position)',
}

// Apenas TFs mandantes (alvo de correções)
const MANDATE_TFS: TimeframeKey[] = ['60','240','720','D','W','M']

export default function PlanOpportunityLimitsForm({ plan, onChanged }: Props) {
  const [limits, setLimits] = React.useState<OpportunityLimits>(() => getLimitsFromPlan(plan || undefined))
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)

  React.useEffect(() => {
    setLimits(getLimitsFromPlan(plan || undefined))
    setError(null); setSaved(false)
  }, [plan?.id])

  const total = Math.max(0, Math.floor(Number(limits.total || 0)))
  const alloc = limits.allocations || {}
  const allocUi = Object.fromEntries(
    MANDATE_TFS.map((k) => [k, Math.floor(Number((alloc as any)[k] || 0))])
  ) as Partial<Record<TimeframeKey, number>>
  const { ok, delta } = validateAllocations(total, allocUi)

  function setCycle(v: OpportunityCycle) { setLimits((p) => ({ ...p, active_cycle: v })) }
  function setMode(v: 'unlimited'|'fixed') { setLimits((p) => ({ ...p, mode: v })) }
  function setTotal(v: number) { setLimits((p) => ({ ...p, total: Math.max(0, Math.floor(v)) })) }
  function setAlloc(tf: TimeframeKey, v: number) {
    setLimits((p) => ({ ...p, allocations: { ...(p.allocations || {}), [tf]: Math.max(0, Math.floor(v)) } }))
  }
  function autoDistribute() {
    const keys: TimeframeKey[] = ['60','240','720','D','W','M']
    const n = Math.max(1, keys.length)
    let base = Math.floor(total / n)
    let rest = total - (base * n)
    const next: Partial<Record<TimeframeKey, number>> = {}
    for (const k of keys) { next[k] = base + (rest > 0 ? 1 : 0); if (rest > 0) rest -= 1 }
    setLimits((p) => ({ ...p, allocations: next }))
  }

  async function onSave() {
    if (!plan?.id) return
    setSaving(true); setError(null); setSaved(false)
    try {
      // Sanitiza para salvar somente TFs mandantes
      const filteredAlloc = Object.fromEntries(
        MANDATE_TFS.map((k) => [k, Math.floor(Number((limits.allocations as any)?.[k] || 0))])
      )
      const payload: any = { opportunity_limits: { ...limits, allocations: filteredAlloc } }
      await updatePlanMeta(plan.id, payload)
      setSaved(true)
      try { onChanged && onChanged() } catch {}
    } catch (e:any) {
      setError(e?.message || 'Falha ao salvar limites')
    } finally { setSaving(false) }
  }

  return (
    <div className="rounded-lg border border-border p-4 mt-4">
      <div className="mb-2 text-lg font-semibold">Limites de Oportunidades</div>
      {!plan?.id && (
        <div className="mb-3 text-sm text-foreground/70">Salve o plano primeiro para habilitar os limites.</div>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label>Ciclo</Label>
          <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={limits.active_cycle}
            onChange={(e)=> setCycle(e.target.value as OpportunityCycle)} disabled={!plan?.id}>
            <option value="day">Diário</option>
            <option value="week">Semanal</option>
            <option value="month">Mensal</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Modo</Label>
          <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={limits.mode}
            onChange={(e)=> setMode(e.target.value as any)} disabled={!plan?.id}>
            <option value="unlimited">Ilimitado</option>
            <option value="fixed">Limitado</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Total do ciclo</Label>
          <Input type="number" min="0" step="1" value={total} onChange={(e)=> setTotal(parseInt(e.target.value || '0',10))} disabled={!plan?.id || limits.mode !== 'fixed'} />
        </div>
      </div>

      <Separator className="my-4" />

      <div className="mb-2 text-sm font-medium">Distribuição por tipo (TF alvo)</div>
      <div className="grid gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {MANDATE_TFS.map((tf) => (
          <div key={tf} className="space-y-1.5">
            <Label>{TF_LABEL[tf]}</Label>
            <Input type="number" min="0" step="1" value={Math.floor(Number((allocUi as any)[tf] || 0))}
              onChange={(e)=> setAlloc(tf, parseInt(e.target.value || '0',10))} disabled={!plan?.id || limits.mode !== 'fixed'} />
          </div>
        ))}
      </div>

      {limits.mode === 'fixed' && (
        <div className="mt-3 text-sm">
          {ok ? (
            <span className="text-emerald-500">Alocação ok • soma = total ({total}).</span>
          ) : (
            <span className="text-amber-500">Soma das alocações difere do total. Ajuste {delta > 0 ? `+${delta}` : `${delta}` }.</span>
          )}
        </div>
      )}

      {error && <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}
      {saved && <div className="mt-3 rounded-md border border-emerald-300/30 bg-emerald-300/10 p-2 text-sm text-emerald-400">Limites salvos.</div>}

      <div className="mt-4 flex items-center gap-2">
        <Button type="button" variant="secondary" onClick={autoDistribute} disabled={!plan?.id || limits.mode !== 'fixed'}>Auto-distribuir</Button>
        <Button type="button" onClick={onSave} disabled={!plan?.id || saving || (limits.mode === 'fixed' && !ok)}>{saving ? 'Salvando...' : 'Salvar limites'}</Button>
      </div>
    </div>
  )
}
